import React, { FC, useCallback, useEffect, useReducer, useRef } from 'react';

import {
  DataGroupApiCreateDataGroupRequest,
  DataGroupApiGetClaimedResourcesRequest,
  DataGroupItemCreate,
  DataGroupOwnerSource,
} from '@ariksa/abac/api';
import { NativeResources } from '@ariksa/inventory-core';
import { InsightV2ApiInsightV2AllRequest } from '@ariksa/inventory-core/api';
import { CloudProvider } from '@ariksa/reporting/api';
import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { each, filter, find, isEmpty, toArray } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { QueryStatus } from 'services/types';
import { customTheme } from 'theme';
import { v4 as uuidv4 } from 'uuid';

import { InventoryService } from 'api/services';
import { Card, FormActionErrorMessage, getIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { Form, FormAction, PrimaryButton, Select } from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { DataGroupIcon } from 'components/Icons';
import {
  addConditionButtonStyles,
  conditionsSelectorStyles,
  dataPolicyFormStyles,
} from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';
import {
  renderDataAccessFormLabel,
  renderDeleteAttribute,
} from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

import { selectDataGroups } from '../../selectors';
import { actions } from '../../slice';

import {
  findClaimedDatabase,
  hasClaimedSchemas,
  isDatabaseHeldWhole,
  isSchemaClaimed,
  isWholeDatabaseClaimed,
} from './utils';

interface Props {
  action?: FormAction;
}

export const CreateDataGroup: FC<Props> = props => {
  const { action = 'Add' } = props;
  const dispatch = useDispatch();
  const {
    cloudAccounts,
    claimedResources,
    dataGroupAction,
    dataGroup,
    resources,
    oktaUsers,
  } = useSelector(selectDataGroups);
  const { accountMapping } = useCloudAccountId();
  const navigate = useNavigate();
  const { setLearnMore } = useKnowledgeBase();

  const [state, updateState] = useReducer(
    (prev, next) => {
      if (typeof next === 'function') {
        // functional update
        return next(prev);
      }
      // normal object merge
      return { ...prev, ...next };
    },
    {
      name: '',
      description: '',
      accountOptions: [],
      ownerOptions: [],
      account: {},
      databases: {},
      resourceOptions: {},
      // Schema options are scoped per database (keyed by the database uuid),
      // so each row shows only the schemas that belong to its selected database.
      schemaOptionsByDb: {},
      schemaLoadingByDb: {},
      isEdit: false,
      isInvalid: false,
    },
  );

  // Tracks which database uuids have already had their schemas fetched, so the
  // same database selected in multiple rows triggers only one request.
  const fetchedSchemasRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    if (action === 'Update') updateState({ isEdit: true });
  }, [action]);

  //get accounts
  useEffect(() => {
    dispatch(
      actions.getCloudAccounts({
        q: {},
      }),
    );
  }, [dispatch, accountMapping]);

  useEffect(() => {
    dispatch(
      actions.getOktaUsers({
        q: {
          insightV2RequestBase: {
            resource_type: NativeResources.OktaUser,
          },
        },
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const ownerOptions = map(oktaUsers.data?.items, user => {
      const ownerName = user?.name;

      return {
        label: ownerName,
        value: user?.uuid,
        data: {
          id: user?.uuid,
          name: ownerName,
          email: user?.email ?? user?.name,
          source: DataGroupOwnerSource.Okta,
          identity_provider_id: user?.account,
        },
      };
    });

    updateState(prev => ({
      ...prev,
      ownerOptions,
      owner: prev.owner ?? ownerOptions[0],
    }));
  }, [oktaUsers.data]);

  useEffect(() => {
    if (!state.account?.value) return;
    dispatch(
      actions.getDataGroups({
        q: { accountId: state.account?.value, page: 1, size: 100 },
      }),
    );
  }, [dispatch, accountMapping, state.account]);

  useEffect(() => {
    const accountId = state.account?.value;
    const dataGroupId = dataGroup.data?.uuid;
    if (!accountId || (state.isEdit && !dataGroupId)) return;

    const query: DataGroupApiGetClaimedResourcesRequest = { accountId };
    if (state.isEdit) query.dataGroupId = dataGroupId;

    dispatch(actions.getClaimedResources({ q: query }));
  }, [dispatch, dataGroup.data?.uuid, state.account?.value, state.isEdit]);

  //set cloud account options
  useEffect(() => {
    const accounts = map(
      filter(cloudAccounts.data, a => a.provider === CloudProvider.Snowflake),
      o => ({
        label:
          accountMapping?.[o?.account_id]?.name +
          ' (' +
          accountMapping?.[o?.account_id]?.cloud_account_id +
          ')',
        value: o?.account_id,
        data: o,
        icon: getIcon(o?.provider!),
      }),
    );
    updateState({ accountOptions: accounts });
  }, [cloudAccounts.data, accountMapping]);

  //set an account
  useEffect(() => {
    if (state.isEdit)
      updateState({
        account: find(
          state.accountOptions,
          o => dataGroup.data?.account_id === o.value,
        ),
      });
    else {
      const id = uuidv4();
      updateState({
        account: state.accountOptions?.[0],
        databases: { [id]: { schemas: [], id } },
      });
    }
  }, [state.accountOptions, state.isEdit, dataGroup.data]);

  useEffect(() => {
    if (state.isEdit && !isEmpty(dataGroup.data))
      updateState({
        name: dataGroup.data.name,
        description: dataGroup.data.description,
        team: dataGroup.data.team,
      });
  }, [state.isEdit, dataGroup.data]);

  useEffect(() => {
    if (isEmpty(state.ownerOptions)) return;

    const currentOwner = dataGroup.data?.owner;
    if (!state.isEdit || !currentOwner) return;

    const ownerId = currentOwner.id;
    if (state.owner?.value?.id === ownerId) return;

    const ownerOption = find(
      state.ownerOptions,
      option => option?.value === ownerId,
    ) ?? {
      label: currentOwner.name,
      value: currentOwner,
      data: currentOwner,
    };

    updateState({ owner: ownerOption });
  }, [
    dataGroup.data,
    state.isEdit,
    state.owner?.value?.id,
    state.ownerOptions,
  ]);

  //get resource options
  const setResourceOptions = (
    res: Record<string, any>,
    resourceType: string,
    isScope: boolean,
  ) => {
    const options = map(res?.data?.items, r => ({
      label: r?.name ?? r?.resource_id,
      value: r?.resource_id,
      icon: getIcon(r?.native_name),
      data: r,
    }));
    updateState(prev => ({
      ...prev,
      resourceOptions: {
        ...prev.resourceOptions,
        [resourceType]: isScope
          ? [{ label: 'All', value: 'all' }, ...options]
          : resourceType === NativeResources.SimpleStorageService
          ? [{ label: '*', value: 'all' }, ...options]
          : options,
      },
    }));
  };

  //get resources like databases, tables, user or role names
  const getResources = useCallback(
    (
      resourceType,
      isScope = false,
      parentResourceType = undefined,
      parentUUID = undefined,
    ) => {
      const insightPayload: InsightV2ApiInsightV2AllRequest = {
        insightV2RequestBase: {
          resource_type: resourceType as NativeResources,
          account_id: [state?.account?.value],
          parent_uuid: parentUUID,
          parent_resource_type: parentResourceType! as NativeResources,
        },
      };
      dispatch(
        actions.getResources({
          q: insightPayload,
          status: QueryStatus.pending,
        }),
      );
      InventoryService.InsightV2.insightV2All(insightPayload).then(res => {
        //set resource options
        setResourceOptions(res, resourceType, isScope);
        dispatch(
          actions.getResources({
            q: insightPayload,
            status: QueryStatus.fulfilled,
            data: res.data?.items,
          }),
        );
      });
    },
    [dispatch, state?.account],
  );

  //get databases (schemas are fetched per database on selection)
  useEffect(() => {
    if (!!state.account?.value) {
      getResources(NativeResources.SnowflakeDatabases);
    }
  }, [state.account, getResources]);

  //fetch schemas scoped to one database via the parent linkage
  const getSchemasForDatabase = useCallback(
    (database: Record<string, any> | undefined) => {
      const dbUuid = database?.uuid;
      if (!dbUuid || !state?.account?.value) return;
      // Fetch each database's schemas at most once (retried on failure below).
      if (fetchedSchemasRef.current[dbUuid]) return;
      fetchedSchemasRef.current[dbUuid] = true;

      updateState(prev => ({
        ...prev,
        schemaLoadingByDb: { ...prev.schemaLoadingByDb, [dbUuid]: true },
      }));

      const insightPayload: InsightV2ApiInsightV2AllRequest = {
        insightV2RequestBase: {
          resource_type: NativeResources.SnowflakeSchemas,
          account_id: [state?.account?.value],
          parent_uuid: dbUuid,
          parent_resource_type: NativeResources.SnowflakeDatabases,
        },
      };
      InventoryService.InsightV2.insightV2All(insightPayload)
        .then(res => {
          const options = map(res?.data?.items, r => {
            const schemaName = r?.name?.split('.')?.[1] ?? r?.name;

            return {
              label: schemaName ?? r?.resource_id,
              value: schemaName ?? r?.resource_id,
              icon: getIcon(r?.native_name),
              data: r,
            };
          });
          updateState(prev => ({
            ...prev,
            schemaOptionsByDb: {
              ...prev.schemaOptionsByDb,
              [dbUuid]: options,
            },
            schemaLoadingByDb: { ...prev.schemaLoadingByDb, [dbUuid]: false },
          }));
        })
        .catch(() => {
          // Allow a later reselection to retry the failed fetch.
          fetchedSchemasRef.current[dbUuid] = false;
          updateState(prev => ({
            ...prev,
            schemaLoadingByDb: { ...prev.schemaLoadingByDb, [dbUuid]: false },
          }));
        });
    },
    [state?.account],
  );

  //set databases and schemas (edit view is read-only, so build chips directly)
  useEffect(() => {
    if (!!state.account && !!state.isEdit) {
      let databases: Record<string, any> = {};
      each(dataGroup.data?.data_group_resources, o => {
        const id = uuidv4();
        databases[id] = {
          id,
          value: find(
            state.resourceOptions?.[NativeResources.SnowflakeDatabases],
            d => d.label === o.name,
          ),
          schemas: map(o.schemas, (schema: Record<string, any>) => {
            const schemaName = schema?.name?.split('.')?.[1] ?? schema?.name;

            return {
              label: schemaName,
              value: schemaName,
            };
          }),
        };
      });
      updateState({ databases });
    }
  }, [
    state.account,
    state.isEdit,
    state.resourceOptions?.[NativeResources.SnowflakeDatabases],
    dataGroup.data,
  ]);

  // Populate schema options for databases restored while editing a data group.
  useEffect(() => {
    if (!state.isEdit) return;

    each(state.databases, database => {
      getSchemasForDatabase(database?.value?.data);
    });
  }, [getSchemasForDatabase, state.databases, state.isEdit]);

  const onDeleteAttribute = item =>
    updateState(prev => {
      const { [item.id]: _, ...rest } = prev?.databases;
      return {
        ...prev,
        databases: rest,
        isInvalid: false,
      };
    });

  const getResourceName = resource =>
    resource?.data?.name ?? resource?.label ?? resource?.value;

  // Claims are matched ignoring case, as the server matches them.
  const getClaimedDatabase = database =>
    findClaimedDatabase(claimedResources.data, getResourceName(database));

  const getDatabaseOptions = (currentDatabaseId: string) => {
    const selectedValues = new Set(
      Object.values(state.databases)
        .filter(database => database.id !== currentDatabaseId)
        .map(database => database.value?.value),
    );
    const databaseOptions =
      state.resourceOptions?.[NativeResources.SnowflakeDatabases] ?? [];

    // Edit mode filters too: claims exclude the group being edited, and the
    // server rejects any resource another group holds.
    return databaseOptions.filter(
      option =>
        !isWholeDatabaseClaimed(getClaimedDatabase(option)) &&
        !selectedValues.has(option.value),
    );
  };

  const getSchemaOptions = database => {
    const schemaOptions = state.schemaOptionsByDb?.[database?.data?.uuid] ?? [];
    const claimedDatabase = getClaimedDatabase(database);

    // Edit mode filters too, for the same reason as databases.
    return schemaOptions.filter(
      option =>
        ![option?.data?.name, option?.label, option?.value].some(name =>
          isSchemaClaimed(claimedDatabase, name),
        ),
    );
  };

  // A database picked without schemas means the whole database, which is not
  // allowed while another group holds some of its schemas. Returns the first
  // such database name, unless the group already held it whole before.
  const getWholeDatabaseConflict = () => {
    const conflict = find(
      state.databases,
      database =>
        !!database?.value &&
        isEmpty(database?.schemas) &&
        hasClaimedSchemas(getClaimedDatabase(database.value)) &&
        !(
          state.isEdit &&
          isDatabaseHeldWhole(
            dataGroup.data?.items,
            getResourceName(database.value),
          )
        ),
    );
    return conflict ? getResourceName(conflict.value) : undefined;
  };
  const wholeDatabaseConflict = getWholeDatabaseConflict();

  //render attributes like user names, role names, tag key pairs
  const renderAttributes = item => {
    return (
      <HStack align="flex-start">
        <Box borderRadius={6} bg="white" w="50%">
          <Select
            options={getDatabaseOptions(item.id)}
            value={state?.databases?.[item?.id]?.value}
            showClearIndicator={false}
            placeholder="Select database..."
            onChange={val => {
              updateState(prev => ({
                ...prev,
                databases: {
                  ...prev?.databases,
                  // Reset schemas: they belong to the previous database.
                  [item.id]: { ...item, value: val, schemas: [] },
                },
                isInvalid: false,
              }));
              getSchemasForDatabase(val?.data);
            }}
            // Claims filter options in edit mode too, so wait for them there.
            isLoading={
              resources?.[NativeResources.SnowflakeDatabases]?.isLoading ||
              claimedResources.isLoading
            }
          />
        </Box>
        <Box borderRadius={6} w="50%">
          <Select
            options={getSchemaOptions(state?.databases?.[item?.id]?.value)}
            value={state?.databases?.[item?.id]?.schemas}
            showClearIndicator={false}
            isMulti
            showTotalSelected
            showSelectedAtBottom
            selectedMessage={
              'schema' +
              (state?.databases?.[item?.id]?.schemas?.length === 1 ? '' : 's') +
              ' selected'
            }
            // Other groups hold some schemas, so the whole database cannot be
            // added and schemas must be picked.
            placeholder={
              hasClaimedSchemas(
                getClaimedDatabase(state?.databases?.[item?.id]?.value),
              )
                ? 'Select schemas (required)...'
                : 'Select schemas...'
            }
            // A database must be selected first; edit view is read-only.
            isDisabled={!state?.databases?.[item?.id]?.value?.data?.uuid}
            styles={conditionsSelectorStyles.select}
            onChange={val =>
              updateState(prev => ({
                ...prev,
                databases: {
                  ...prev?.databases,
                  [item.id]: { ...item, schemas: val },
                },
                isInvalid: false,
              }))
            }
            isLoading={
              !!state.schemaLoadingByDb?.[
                state?.databases?.[item?.id]?.value?.data?.uuid
              ]
            }
          />
        </Box>
        <Box pt={1}>
          {renderDeleteAttribute(
            () => onDeleteAttribute(item),
            toArray(state.databases)?.length === 1,
          )}
        </Box>
      </HStack>
    );
  };

  const onSubmit = type => {
    let databases: DataGroupItemCreate[] = [];
    each(state.databases, database => {
      const databaseName = database?.value?.value;
      if (!databaseName) return;
      if (isEmpty(database?.schemas)) {
        // DB-only entry: no schema selected means the whole database.
        databases.push({ database_name: databaseName });
      } else {
        each(database?.schemas, (schema: Record<string, any>) =>
          databases.push({
            database_name: databaseName,
            schema_name: schema?.value,
          }),
        );
      }
    });
    const payload: DataGroupApiCreateDataGroupRequest = {
      dataGroupCreate: {
        description: state.description,
        name: state.name,
        account_id: state.account?.value,
        items: databases,
        team: state.team,
        owner: state?.owner?.data,
      },
    };

    const onSuccess = () => {
      updateState({ isInvalid: false });
      navigate('/jit/data/group');
    };
    const onError = () => updateState({ isInvalid: true });

    if (state.isEdit)
      dispatch(
        actions.updateDataGroup({
          q: {
            dataGroupId: dataGroup.data?.uuid,
            dataGroupUpdate: {
              name: state.name,
              description: state.description,
              items: databases,
              team: state.team,
              owner: state?.owner?.data,
            },
          },
          onSuccess,
          onError,
        }),
      );
    else dispatch(actions.createDataGroup({ q: payload, onSuccess, onError }));
  };

  const onClickAddEntry = () => {
    const id = uuidv4();
    updateState(prev => ({
      ...prev,
      databases: { ...prev.databases, [id]: { id, schemas: [] } },
    }));
  };

  return (
    <Card styles={{ cardContent: { pl: 32, ml: 2 } }}>
      <Form
        title={
          <HStack justify="space-between" w="full">
            <Box>{state.isEdit ? 'Update' : 'Add'} Data Group</Box>
            <LearnMoreButton
              onOpen={() => setLearnMore('data_group')}
              label="Data Group"
            />
          </HStack>
        }
        titleIcon={<DataGroupIcon />}
        schema={{
          name: {
            type: 'text',
            label: 'Data Group Name',
            value: state.name,
            onChange: val => {
              updateState({
                name: val,
                isInvalid: false,
              });
            },
            //isDisabled: state.isEdit,
            placeholder: 'Enter data group name',
            isRequired: true,
          },
          description: {
            type: 'textArea',
            label: 'Description',
            placeholder:
              'Describe the purpose of this data group along with details on ' +
              'specific data types that you are aggregating here',
            isRequired: true,
            onChange: val =>
              updateState({ description: val, isInvalid: false }),
            value: state.description,
          },
          teamAndOwner: {
            type: 'object',
            styles: {
              objectContainer: {
                align: 'start',
                direction: 'row',
                spacing: 4,
                w: 'full',
              },
              propertyContainer: { w: '50%' },
            },
            properties: {
              team: {
                type: 'text',
                label: 'Team',
                value: state.team,
                onChange: team => updateState({ team }),
                placeholder: 'Enter team name',
              },
              owner: {
                type: 'react-select',
                label: 'Owner',
                options: state.ownerOptions,
                value: state.owner,
                isLoading: oktaUsers.isLoading,
                onChange: owner => updateState({ owner }),
                placeholder: 'Choose owner',
              },
            },
          },
          account: {
            type: 'react-select',
            label: 'Account',
            options: state.accountOptions,
            value: state.account,
            isLoading: cloudAccounts.isLoading,
            isDisabled: cloudAccounts.isLoading || state.isEdit,
            onChange: option => {
              const id = uuidv4();
              // Schemas are account-scoped; drop caches from the old account.
              fetchedSchemasRef.current = {};
              updateState({
                account: option,
                databases: { [id]: { schemas: [], id } },
                schemaOptionsByDb: {},
                schemaLoadingByDb: {},
                isInvalid: false,
              });
            },
            isRequired: true,
            showIconInValueContainer: true,
          },
          divider: {
            type: 'custom',
            component: () => (
              <Box borderBottom="1px solid" borderColor="componentBorder" />
            ),
          },
          databasesAndSchemas: {
            type: 'custom',
            isRequired: true,
            component: () => (
              <Stack>
                <HStack justify="space-between">
                  <HStack spacing={0}>
                    <Box fontWeight={600}>Add Databases and Schemas</Box>
                    <Text color="red">*</Text>
                  </HStack>
                  <Flex justify="flex-end">
                    <PrimaryButton
                      {...addConditionButtonStyles}
                      onClick={() => onClickAddEntry()}
                    >
                      + ADD
                    </PrimaryButton>
                  </Flex>
                </HStack>
                <Stack
                  bg={customTheme.colors.gray['50']}
                  borderRadius={6}
                  spacing={5}
                  py={6}
                  px={6}
                >
                  <HStack>
                    {renderDataAccessFormLabel('Databases', '50%')}
                    {renderDataAccessFormLabel('Schemas', '50%')}
                  </HStack>
                  {map(state.databases, o => renderAttributes(o))}
                </Stack>
              </Stack>
            ),
          },
          // Create/update failures land in dataGroupAction, not dataGroup, so
          // check that error; otherwise server rejections were never shown.
          ...(state.isInvalid && !isEmpty(dataGroupAction.error)
            ? {
                validatedMessage: {
                  type: 'custom',
                  component: () => (
                    <FormActionErrorMessage
                      description={dataGroupAction.error?.description}
                    />
                  ),
                },
              }
            : {}),
        }}
        showHelp
        buttonOptions={{
          submit: {
            name: state.isEdit ? 'UPDATE' : 'ADD',
            isLoading: dataGroup.isLoading || dataGroupAction.isLoading,
            // Also blocked while a database would be added whole although
            // another group holds some of its schemas.
            isDisabled:
              !state.name || !state.description || !!wholeDatabaseConflict,
            tooltip: !state.name
              ? 'Please provide name'
              : !state.description
              ? 'Please provide description'
              : wholeDatabaseConflict
              ? `Select schemas for ${wholeDatabaseConflict}: other data ` +
                'groups hold some of its schemas'
              : undefined,
          },
          reset: {
            name: 'CANCEL',
            onClick: () => navigate('/jit/data/group'),
            isVisible: true,
          },
        }}
        styles={dataPolicyFormStyles}
        handleSubmit={() => onSubmit('submit')}
      />
      <LearnMore />
    </Card>
  );
};
